import { useState, useEffect } from 'react'
import { Canvas } from '@react-three/fiber'
import { motion } from 'framer-motion'
import Scene3D from './Scene3D'
import ContactPopup from './ContactPopup'

/**
 * HeroSection - Cinematic Hero Component
 * 
 * Features:
 * - Full-screen 3D background with gradient mesh and particles
 * - Arabic headline with fade-in animation (2s duration)
 * - English subheadline with staggered delay (2.3s)
 * - CTA button with hover scale + glow effects
 * - Subtle parallax scrolling effect
 * - Logo integration with fade-in
 * 
 * Animation Timeline:
 * 0.0s: 3D scene starts loading
 * 0.5s: Logo fades in
 * 1.0s: Arabic headline starts fade-in
 * 2.0s: English subheadline starts fade-in
 * 2.5s: CTA button becomes interactive
 * 
 * Performance Notes:
 * - Canvas uses transparent background for overlay effects
 * - Parallax uses transform3d for GPU acceleration
 * - Hover effects use CSS transforms for 60fps
 */

export default function HeroSection() {
  const [scrollY, setScrollY] = useState(0)
  const [isLoaded, setIsLoaded] = useState(false)
  const [showContactPopup, setShowContactPopup] = useState(false)
  
  // Parallax scroll effect
  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY
      setScrollY(scrollY)
      document.documentElement.style.setProperty('--scroll-y', `${scrollY * 0.3}px`)
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])
  
  // Trigger loaded state for animations
  useEffect(() => {
    const timer = setTimeout(() => setIsLoaded(true), 500)
    return () => clearTimeout(timer)
  }, [])
  
  return (
    <>
      {/* Scroll Progress Indicator */}
      <motion.div
        className="fixed top-0 left-0 right-0 h-1 bg-luxury-gold/20 z-50"
        style={{ scaleX: scrollY / (window.innerHeight * 0.5) }}
        initial={{ scaleX: 0 }}
        animate={{ scaleX: Math.min(scrollY / (window.innerHeight * 0.5), 1) }}
        transition={{ duration: 0.1 }}
      />
      
      <motion.section 
        className="relative h-screen sm:h-screen md:h-screen lg:h-screen w-full overflow-hidden" 
        id="hero-section"
        style={{
          opacity: Math.max(0, 1 - scrollY / (window.innerHeight * 0.8)),
          transform: `translateY(${scrollY * 0.3}px)`
        }}
      >
      {/* 3D Background Canvas */}
      <div className="absolute inset-0">
        <Canvas
          camera={{ position: [0, 0, 5], fov: 75 }}
          dpr={[1, 2]} // Performance optimization for mobile
          style={{ background: 'transparent' }}
        >
          <Scene3D />
        </Canvas>
      </div>
      
      {/* Overlay gradient for depth */}
      <div className="absolute inset-0 bg-gradient-to-b from-luxury-black/20 via-transparent to-luxury-black/40" />
      
        {/* Logo Container - Independent positioning */}
        <div className="absolute top-16 sm:top-12 md:top-16 lg:top-16 left-1/2 transform -translate-x-1/2 z-50">
         {/* Logo - DOMINANT Brand Presence */}
         <motion.div
           initial={{ opacity: 0, scale: 0.8, y: 30 }}
           animate={{ 
             opacity: isLoaded ? 1 : 0, 
             scale: isLoaded ? 1 : 0.8,
             y: isLoaded ? 0 : 30 
           }}
           transition={{ duration: 1.5, delay: 0.3, ease: "easeOut" }}
           className="mb-0 relative z-50"
           style={{
             zIndex: 50,
             position: 'relative'
           }}
         >
           <div className="relative">

             {/* Sparkle Effects */}
             <motion.div
               className="absolute inset-0"
               animate={{
                 opacity: [0, 1, 0]
               }}
               transition={{
                 duration: 1.5,
                 repeat: Infinity,
                 ease: "easeInOut"
               }}
             >
               {[...Array(8)].map((_, i) => (
        <motion.div
                   key={i}
                   className="absolute w-2 h-2"
                   style={{
                     left: `${15 + (i * 10)}%`,
                     top: `${25 + (i * 8)}%`,
                   }}
                   animate={{
                     rotate: [0, 360],
                     scale: [0, 1, 0],
                     opacity: [0, 1, 0]
                   }}
                   transition={{
                     duration: 2,
                     repeat: Infinity,
                     ease: "easeInOut",
                     delay: i * 0.3
                   }}
                 >
                   <div className="w-full h-full bg-gradient-to-r from-yellow-400 to-pink-500 rounded-full blur-sm" />
                 </motion.div>
               ))}
             </motion.div>

             {/* Holographic Depth Effect */}
             <motion.div
               className="absolute inset-0 rounded-full"
               animate={{
                 background: [
                   "radial-gradient(circle, rgba(255,0,255,0.1) 0%, transparent 70%)",
                   "radial-gradient(circle, rgba(0,255,255,0.1) 0%, transparent 70%)",
                   "radial-gradient(circle, rgba(255,255,0,0.1) 0%, transparent 70%)",
                   "radial-gradient(circle, rgba(255,0,255,0.1) 0%, transparent 70%)"
                 ]
               }}
               transition={{
                 duration: 4,
                 repeat: Infinity,
                 ease: "easeInOut"
               }}
             />

             {/* Random Radiation Glowing Ring */}
             <motion.div
               className="absolute inset-0 rounded-full"
               animate={{
                 boxShadow: [
                   "0 0 20px rgba(212, 175, 55, 0.4), 0 0 40px rgba(212, 175, 55, 0.3), 0 -12px 35px rgba(212, 175, 55, 0.6)",
                   "0 0 25px rgba(212, 175, 55, 0.5), 0 0 50px rgba(212, 175, 55, 0.4), 0 -8px 30px rgba(212, 175, 55, 0.7)",
                   "0 0 15px rgba(212, 175, 55, 0.3), 0 0 30px rgba(212, 175, 55, 0.2), 0 -15px 40px rgba(212, 175, 55, 0.5)",
                   "0 0 30px rgba(212, 175, 55, 0.6), 0 0 60px rgba(212, 175, 55, 0.5), 0 -5px 25px rgba(212, 175, 55, 0.8)",
                   "0 0 18px rgba(212, 175, 55, 0.4), 0 0 35px rgba(212, 175, 55, 0.3), 0 -10px 32px rgba(212, 175, 55, 0.6)"
                 ]
               }}
               transition={{
                 duration: 4,
                 repeat: Infinity,
                 ease: "easeInOut"
               }}
             />
             
             {/* Color-Changing Border Ring */}
             <motion.div
               className="absolute inset-0 rounded-full border-2"
               animate={{
                 opacity: [0.4, 0.8, 0.4],
                 scale: [1, 1.05, 1],
                 borderColor: [
                   "rgba(212, 175, 55, 1)",
                   "rgba(255, 105, 180, 1)",
                   "rgba(0, 191, 255, 1)",
                   "rgba(50, 205, 50, 1)",
                   "rgba(255, 165, 0, 1)"
                 ],
                 boxShadow: [
                   "0 0 5px rgba(212, 175, 55, 0.3)",
                   "0 0 10px rgba(255, 105, 180, 0.4)",
                   "0 0 5px rgba(0, 191, 255, 0.3)",
                   "0 0 10px rgba(50, 205, 50, 0.4)",
                   "0 0 5px rgba(255, 165, 0, 0.3)"
                 ]
               }}
               transition={{
                 duration: 10,
                 repeat: Infinity,
                 ease: "easeInOut"
               }}
             />
             
             {/* Main Logo with Subtle Effects */}
            <motion.img
            src="/logo.png" 
            alt="MindGlow Logo" 
              className="w-28 h-28 sm:w-32 sm:h-32 md:w-36 md:h-36 lg:w-44 lg:h-44 object-contain relative z-10"
               animate={{
                 filter: [
                   "drop-shadow(0 0 8px rgba(212, 175, 55, 0.3)) drop-shadow(0 0 15px rgba(255, 255, 255, 0.1)) drop-shadow(0 -5px 12px rgba(212, 175, 55, 0.4))",
                   "drop-shadow(0 0 10px rgba(212, 175, 55, 0.4)) drop-shadow(0 0 20px rgba(255, 255, 255, 0.15)) drop-shadow(0 -6px 15px rgba(212, 175, 55, 0.5))",
                   "drop-shadow(0 0 8px rgba(212, 175, 55, 0.3)) drop-shadow(0 0 15px rgba(255, 255, 255, 0.1)) drop-shadow(0 -5px 12px rgba(212, 175, 55, 0.4))"
                 ],
                 scale: [1, 1.02, 1, 1.01, 1]
               }}
               transition={{
                 duration: 3,
                 repeat: Infinity,
                 ease: "easeInOut"
               }}
               whileHover={{
                 scale: 1.08,
                 filter: "drop-shadow(0 0 60px rgba(212, 175, 55, 1.0)) drop-shadow(0 0 100px rgba(255, 255, 255, 0.4))",
                 transition: { duration: 0.4, ease: "easeOut" }
               }}
               style={{
                 zIndex: 1000,
                 position: 'relative'
               }}
             />
           </div>
        </motion.div>
       </div>
        
       {/* Title and Description Container - Full width with centered elements */}
        <div className="absolute top-1/2 translate-y-2 sm:translate-y-4 left-0 w-full h-56 sm:h-56 md:h-64 lg:h-64 flex flex-col items-center justify-center z-10 px-4 sm:px-6 md:px-6 lg:px-6">
        {/* Arabic Headline */}
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: isLoaded ? 1 : 0, y: isLoaded ? 0 : 30 }}
          transition={{ duration: 2, delay: 1 }}
            className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-luxury-gold mb-4 sm:mb-3 md:mb-4 leading-tight -mt-2 sm:-mt-6 md:-mt-8 text-center"
           style={{
             fontFamily: "'Amiri', 'Scheherazade New', 'Noto Naskh Arabic', serif",
             textShadow: "0 0 30px rgba(212, 175, 55, 0.6)",
             letterSpacing: "0.05em"
           }}
         >
        اختر مستوى <span 
          className="bg-gradient-to-r from-yellow-400 via-pink-500 to-purple-600 bg-clip-text text-transparent font-extrabold relative"
          style={{
            backgroundSize: '200% 200%',
            animation: 'gradientShift 3s ease-in-out infinite',
            textShadow: '0 0 15px rgba(255, 255, 255, 0.4)',
            filter: 'drop-shadow(0 0 10px rgba(255, 255, 255, 0.3))'
          }}
        >
          التوهّج
          {/* Animated Double Thin Underlines */}
          <motion.div
            className="absolute bottom-0 left-0 h-0.5 bg-white"
            initial={{ width: 0 }}
            animate={{ width: isLoaded ? '100%' : 0 }}
            transition={{ duration: 2, delay: 2.5, ease: "easeOut" }}
            style={{
              boxShadow: '0 0 8px rgba(255, 255, 255, 0.6)',
              filter: 'drop-shadow(0 0 4px rgba(255, 255, 255, 0.4))',
              opacity: 0.8
            }}
          />
          <motion.div
            className="absolute bottom-1 left-0 h-0.5 bg-white"
            initial={{ width: 0 }}
            animate={{ width: isLoaded ? '85%' : 0 }}
            transition={{ duration: 2.2, delay: 2.7, ease: "easeOut" }}
            style={{
              boxShadow: '0 0 6px rgba(255, 255, 255, 0.4)',
              filter: 'drop-shadow(0 0 3px rgba(255, 255, 255, 0.3))',
              opacity: 0.6
            }}
          />
        </span> الذى يناسبك
        </motion.h1>
        
         {/* Enhanced English Subheadline */}
         <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: isLoaded ? 1 : 0, y: isLoaded ? 0 : 20 }}
          transition={{ duration: 2, delay: 2.3 }}
            className="mb-6 sm:mb-6 md:mb-8 max-w-xs sm:max-w-sm md:max-w-2xl lg:max-w-4xl mt-6 sm:mt-6 md:mt-8 text-center"
         >
          <p
            className="text-xl sm:text-2xl md:text-3xl lg:text-4xl text-luxury-gold font-medium mb-3 sm:mb-3 md:mb-4 leading-relaxed"
            style={{
              fontFamily: "'Cormorant Garamond', 'Playfair Display', serif",
              textShadow: "0 0 30px rgba(212, 175, 55, 0.8)",
              letterSpacing: "0.05em",
              lineHeight: "1.4",
              fontWeight: "500"
            }}
          >
            Standard — what others call
          </p>
          <p
            className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold leading-tight text-luxury-gold"
            style={{
              fontFamily: "'Cormorant Garamond', 'Playfair Display', serif",
              letterSpacing: "0.06em",
              lineHeight: "1.2",
              fontWeight: "700"
            }}
          >
            Max
          </p>
         </motion.div>
        
        {/* Modern Minimalist CTA Button */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ 
            opacity: isLoaded ? 1 : 0, 
            scale: isLoaded ? 1 : 0.9 
          }}
          transition={{ duration: 0.8, delay: 2.5 }}
          className="relative"
        >
        <motion.button
          onClick={() => setShowContactPopup(true)}
          whileHover={{ 
            scale: 1.05,
            y: -2
          }}
          whileTap={{ scale: 0.95 }}
          className="px-8 sm:px-10 md:px-12 lg:px-12 py-4 sm:py-5 md:py-5 lg:py-5 text-xl sm:text-2xl md:text-2xl lg:text-2xl font-bold rounded-lg border-2 border-luxury-gold text-luxury-gold hover:bg-luxury-gold hover:text-luxury-black transition-all duration-300 ease-out"
          style={{
            fontFamily: "'Orbitron', 'Exo 2', 'Rajdhani', sans-serif",
            letterSpacing: '0.1em',
            fontWeight: '700',
            textShadow: '0 0 10px rgba(212, 175, 55, 0.3)',
            boxShadow: '0 0 20px rgba(212, 175, 55, 0.2)'
          }}
        >
          Illuminate
        </motion.button>
        </motion.div>
        
      </div>
       
    </motion.section>
    
    {/* Contact Popup */}
    <ContactPopup 
      isOpen={showContactPopup} 
      onClose={() => setShowContactPopup(false)} 
    />
    </>
  )
}
